import React, { useEffect, useState } from "react";
import { Clock, LogOut, Loader2, Monitor } from "lucide-react";
import { toast } from "sonner";
import { http, errMsg } from "@/lib/api";

// Extracted from pages/Users.jsx (Team & Roles → Sessions tab) so it can
// also be rendered from the Client Portal's Settings → Security tab
// (pages/portal/PortalSettings.jsx) — same GET /users/me/sessions /
// POST /users/me/sessions/{id}/revoke endpoints, same behavior, one
// implementation instead of two that could drift apart.
export default function SessionsPanel() {
    const [sessions, setSessions] = useState([]);
    const [loading, setLoading] = useState(true);

    const refresh = async () => {
        try {
            const { data } = await http.get("/users/me/sessions");
            setSessions(data);
        } catch (e) {
            toast.error(errMsg(e));
        } finally {
            setLoading(false);
        }
    };

    // Same fire-once-on-mount intent as the page-level loader. The
    // refresh closure changes identity each render; including it in
    // deps would loop. eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { refresh(); }, []);

    const revoke = async (sid) => {
        if (!window.confirm("Sign this device out? Anyone using it will need to log in again.")) return;
        try {
            await http.post(`/users/me/sessions/${sid}/revoke`);
            toast.success("Session revoked");
            refresh();
        } catch (e) {
            toast.error(errMsg(e));
        }
    };

    return (
        <div className="border border-border bg-surface">
            <table className="w-full text-sm" data-testid="sessions-table">
                <thead className="border-b border-border">
                    <tr className="font-mono text-[10px] uppercase tracking-widest text-muted-foreground">
                        <th className="px-4 py-2 text-left">Device</th>
                        <th className="px-4 py-2 text-left">IP</th>
                        <th className="px-4 py-2 text-left">Created</th>
                        <th className="px-4 py-2 text-left">Last active</th>
                        <th className="px-4 py-2 text-left">Status</th>
                        <th className="px-4 py-2 text-right">Action</th>
                    </tr>
                </thead>
                <tbody>
                    {loading && (
                        <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">
                            <Loader2 size={14} className="mr-2 inline animate-spin" /> Loading sessions…
                        </td></tr>
                    )}
                    {!loading && sessions.length === 0 && (
                        <tr><td colSpan={6} className="px-4 py-10 text-center text-muted-foreground">
                            No tracked sessions.
                        </td></tr>
                    )}
                    {sessions.map((s) => (
                        <tr key={s.id} className="border-b border-border/60 last:border-0 hover:bg-secondary/30">
                            <td className="px-4 py-2.5">
                                <span className="inline-flex items-center gap-2">
                                    <Monitor size={12} className="text-muted-foreground" />
                                    {s.device_label}
                                    {s.is_current && (
                                        <span
                                            data-testid={`session-current-pill-${s.id}`}
                                            className="ml-1 rounded bg-emerald-500/10 px-1.5 py-0.5 text-[9px] font-semibold uppercase tracking-wider text-emerald-300 border border-emerald-500/30"
                                        >
                                            Current
                                        </span>
                                    )}
                                </span>
                            </td>
                            <td className="px-4 py-2.5 font-mono text-xs">{s.ip}</td>
                            <td className="px-4 py-2.5 font-mono text-[11px] text-muted-foreground">
                                {(s.created_at || "").replace("T", " ").slice(0, 19)}
                            </td>
                            <td className="px-4 py-2.5 font-mono text-[11px] text-muted-foreground">
                                <Clock size={10} className="mr-1 inline" />
                                {(s.last_seen_at || "").replace("T", " ").slice(0, 19)}
                            </td>
                            <td className="px-4 py-2.5">
                                {s.revoked ? (
                                    <span className="mb-pill border-rose-400/40 text-rose-300">revoked</span>
                                ) : (
                                    <span className="mb-pill border-emerald-500/40 text-emerald-400">active</span>
                                )}
                            </td>
                            <td className="px-4 py-2.5 text-right">
                                {!s.revoked && !s.is_current && (
                                    <button
                                        onClick={() => revoke(s.id)}
                                        data-testid={`session-revoke-${s.id}`}
                                        className="rounded-sm border border-border px-2 py-1 font-mono text-[10px] uppercase tracking-widest hover:border-rose-400 hover:text-rose-400"
                                    >
                                        <LogOut size={10} className="mr-1 inline" /> Sign out
                                    </button>
                                )}
                            </td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
    );
}
